import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_STORAGE_KEY = 'finance_session_token';
const REGISTROS_FILTERS_STORAGE_KEY = 'finance_registros_filters';

const clearLocalSessionData = () => {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(REGISTROS_FILTERS_STORAGE_KEY);
  localStorage.removeItem('registros_filters_open');
  // Clear Serafina chat history so next user does not see previous conversation
  try { sessionStorage.removeItem('serafina_messages'); } catch (_e) { /* noop */ }
};

// Inject Authorization header from localStorage on every request as a defensive fallback
// (cookie is the primary mechanism; this ensures auth still works if cookie is blocked)
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      clearLocalSessionData();
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const isValidUser = (data) => {
    return Boolean(data && typeof data === 'object' && !Array.isArray(data) && (data.user_id || data.email));
  };

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      if (isValidUser(response.data)) {
        setUser(response.data);
        setIsAuthenticated(true);
      } else {
        clearLocalSessionData();
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      // Token invalid/expired -> clear local fallback too
      clearLocalSessionData();
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithEmail = async (email, password) => {
    const response = await axios.post(
      `${API}/auth/login-email`,
      { email, password },
      { withCredentials: true }
    );
    if (!isValidUser(response.data)) {
      throw new Error('Resposta de autenticação inválida');
    }
    if (response.data?.session_token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, response.data.session_token);
    }
    localStorage.removeItem(REGISTROS_FILTERS_STORAGE_KEY);
    setUser(response.data);
    setIsAuthenticated(true);
    return response.data;
  };

  const registerWithEmail = async (name, email, password) => {
    const response = await axios.post(
      `${API}/auth/register`,
      { name, email, password },
      { withCredentials: true }
    );
    if (!isValidUser(response.data)) {
      throw new Error('Resposta de cadastro inválida');
    }
    if (response.data?.session_token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, response.data.session_token);
    }
    localStorage.removeItem(REGISTROS_FILTERS_STORAGE_KEY);
    setUser(response.data);
    setIsAuthenticated(true);
    return response.data;
  };

  const forgotPassword = async (email) => {
    const response = await axios.post(`${API}/auth/forgot-password`, { email });
    return response.data;
  };

  const resetPassword = async (email, code, new_password) => {
    const response = await axios.post(`${API}/auth/reset-password`, {
      email,
      code,
      new_password
    });
    return response.data;
  };

  const loginWithGoogle = async (credential) => {
    const response = await axios.post(
      `${API}/auth/google`,
      { credential },
      { withCredentials: true }
    );
    if (!isValidUser(response.data)) {
      throw new Error('Resposta de autenticação Google inválida');
    }
    if (response.data?.session_token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, response.data.session_token);
    }
    localStorage.removeItem(REGISTROS_FILTERS_STORAGE_KEY);
    setUser(response.data);
    setIsAuthenticated(true);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    clearLocalSessionData();
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        isAuthenticated,
        setIsAuthenticated,
        checkAuth,
        loginWithEmail,
        registerWithEmail,
        loginWithGoogle,
        forgotPassword,
        resetPassword,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export { TOKEN_STORAGE_KEY, REGISTROS_FILTERS_STORAGE_KEY };
